'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiService } from '@/lib/api';

/**
 * Contexto global de tasas de cambio del CRM.
 *
 * Fuente única de verdad para todos los cálculos financieros (dashboard,
 * desarrollos, calculadora ROI, etc.). Las tasas se guardan en la tabla
 * `settings` (categoría `finance`) y se cachean en localStorage para que
 * cualquier pantalla arranque con el último valor conocido.
 */

const STORAGE_KEY = 'crm_exchange_rates';

export const DEFAULT_RATES = {
    BUY_EXCHANGE_RATE: '7.60',
    SELL_EXCHANGE_RATE: '7.80',
};

const CurrencyContext = createContext(null);

/** Normaliza y valida las tasas recibidas (siempre positivas y con 2 decimales). */
export function normalizeRates(raw = {}) {
    const pick = (value, fallback) => {
        const num = Number(String(value ?? '').replace(',', '.'));
        return Number.isFinite(num) && num > 0 ? num.toFixed(2) : fallback;
    };
    return {
        BUY_EXCHANGE_RATE: pick(raw.BUY_EXCHANGE_RATE, DEFAULT_RATES.BUY_EXCHANGE_RATE),
        SELL_EXCHANGE_RATE: pick(raw.SELL_EXCHANGE_RATE, DEFAULT_RATES.SELL_EXCHANGE_RATE),
    };
}

export function CurrencyProvider({ children }) {
    const [rates, setRates] = useState(DEFAULT_RATES);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 1. Hidratar desde caché local para pintar sin esperar la red
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) setRates(normalizeRates(JSON.parse(saved)));
        } catch {
            // Caché corrupta: se ignora y se usan los valores por defecto
        }
    }, []);

    const persist = useCallback((next) => {
        setRates(next);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
            // Entornos restringidos: la tasa sigue viva en memoria
        }
    }, []);

    // 2. Traer la configuración oficial del backend
    const refresh = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await apiService.get('/settings/general?category=finance');
            if (Array.isArray(data)) {
                const next = { ...DEFAULT_RATES };
                data.forEach((setting) => {
                    if (setting?.key in next) next[setting.key] = setting.value;
                });
                persist(normalizeRates(next));
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [persist]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // 3. Sincronizar entre pestañas del mismo navegador
    useEffect(() => {
        const onStorage = (event) => {
            if (event.key !== STORAGE_KEY || !event.newValue) return;
            try {
                setRates(normalizeRates(JSON.parse(event.newValue)));
            } catch {
                // Ignorar payload inválido
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // 4. Guardar ambas tasas y notificar al resto de la app
    const saveRates = useCallback(async (nextRates) => {
        const payload = normalizeRates(nextRates);
        await apiService.put('/settings/general/BUY_EXCHANGE_RATE', { value: payload.BUY_EXCHANGE_RATE });
        await apiService.put('/settings/general/SELL_EXCHANGE_RATE', { value: payload.SELL_EXCHANGE_RATE });
        persist(payload);
        window.dispatchEvent(new CustomEvent('currency-rates-updated', { detail: payload }));
        return payload;
    }, [persist]);

    const value = useMemo(() => {
        const buyRate = Number(rates.BUY_EXCHANGE_RATE) || Number(DEFAULT_RATES.BUY_EXCHANGE_RATE);
        const sellRate = Number(rates.SELL_EXCHANGE_RATE) || Number(DEFAULT_RATES.SELL_EXCHANGE_RATE);
        return { rates, buyRate, sellRate, loading, error, refresh, saveRates };
    }, [rates, loading, error, refresh, saveRates]);

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency debe usarse dentro de <CurrencyProvider>');
    }
    return context;
}
